import { AltiusRes } from "store/types";
import type {
  ArticlesPageParams,
  ArticlesPageResponse,
  dailyBrief,
  DigestRow,
  DigestSettingsForm,
  INewsFeedConfigResponse,
  SourceRow,
  TopicRow
} from "store/types/news";

import { UserAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface INewsFeedActions {
  getGuidancePromptAction: (data: string) => Promise<string>;
  getFeedConfigAction: () => Promise<INewsFeedConfigResponse>;
  getArticlesAction: (params: ArticlesPageParams) => Promise<ArticlesPageResponse>;
  CreateSourcesAction: (data: SourceRow) => Promise<SourceRow>;
  UpdateSourcesAction: (data: SourceRow) => Promise<SourceRow>;
  DeleteSourcesAction: (source_url: string) => Promise<SourceRow>;
  UpdateSourcesSelectAction: (data: { source_url: string; priority: number; name: string }) => Promise<SourceRow>;
  RemoveSourcesSelectedAction: (source_url: string) => Promise<SourceRow>;
  CreateTopicsAction: (data: TopicRow) => Promise<TopicRow>;
  UpdateTopicsAction: (data: TopicRow) => Promise<TopicRow>;
  DeleteTopicsAction: (id: number) => Promise<TopicRow>;
  UpdateTopicsSelectAction: (data: { id: number }) => Promise<TopicRow>;
  RemoveTopicsSelectedAction: (id: number) => Promise<TopicRow>;
  UpdateDigestSettingsAction: (data: DigestSettingsForm) => Promise<void>;
  UpdateNewsServiceAction: () => Promise<void>;
  GetDailyBriefAction: () => Promise<dailyBrief | null>;
  GetPastDigestsAction: () => Promise<DigestRow[]>;
}

export class NewsFeedActions extends AsyncActions implements INewsFeedActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getFeedConfigAction: INewsFeedActions["getFeedConfigAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<INewsFeedConfigResponse>>({
      url: "/api/v0.0.3/news-feed/settings"
    });
    return payload;
  };

  public getArticlesAction: INewsFeedActions["getArticlesAction"] = async (params) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ArticlesPageResponse>>({
      url: "/api/v0.0.3/news-feed",
      params
    });
    return payload;
  };

  public getGuidancePromptAction: INewsFeedActions["getGuidancePromptAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<string>>({
      url: "/api/v0.0.3/news-feed/guidance-prompt",
      data: {
        guidance_prompt: data
      }
    });
    return payload;
  };

  public CreateSourcesAction: INewsFeedActions["CreateSourcesAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<SourceRow>>({
      url: "/api/v0.0.3/news-feed/sources",
      data
    });
    return payload;
  };

  public UpdateSourcesAction: INewsFeedActions["UpdateSourcesAction"] = async (data) => {
    const { source_url } = data;
    const {
      data: { data: payload }
    } = await this.api.put<AltiusRes<SourceRow>>({
      url: `/api/v0.0.3/news-feed/sources/${encodeURIComponent(source_url)}`,
      data
    });
    return payload;
  };

  public DeleteSourcesAction: INewsFeedActions["DeleteSourcesAction"] = async (source_url) => {
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<SourceRow>>({
      url: `/api/v0.0.3/news-feed/sources/${encodeURIComponent(source_url)}`
    });
    return payload;
  };

  public UpdateSourcesSelectAction: INewsFeedActions["UpdateSourcesSelectAction"] = async (data) => {
    const { source_url } = data;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<SourceRow>>({
      url: `/api/v0.0.3/news-feed/sources/${encodeURIComponent(source_url)}/select`,
      data
    });
    return payload;
  };

  public RemoveSourcesSelectedAction: INewsFeedActions["RemoveSourcesSelectedAction"] = async (source_url) => {
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<SourceRow>>({
      url: `/api/v0.0.3/news-feed/sources/${encodeURIComponent(source_url)}/select`
    });
    return payload;
  };

  public CreateTopicsAction: INewsFeedActions["CreateTopicsAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<TopicRow>>({
      url: "/api/v0.0.3/news-feed/topics",
      data
    });
    return payload;
  };

  public UpdateTopicsAction: INewsFeedActions["UpdateTopicsAction"] = async (data) => {
    const { id } = data;
    const {
      data: { data: payload }
    } = await this.api.put<AltiusRes<TopicRow>>({
      url: `/api/v0.0.3/news-feed/topics/${id}`,
      data
    });
    return payload;
  };

  public DeleteTopicsAction: INewsFeedActions["DeleteTopicsAction"] = async (id) => {
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<TopicRow>>({
      url: `/api/v0.0.3/news-feed/topics/${id}`
    });
    return payload;
  };

  public UpdateTopicsSelectAction: INewsFeedActions["UpdateTopicsSelectAction"] = async (data) => {
    const { id } = data;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<TopicRow>>({
      url: `/api/v0.0.3/news-feed/topics/${id}/select`
    });
    return payload;
  };

  public RemoveTopicsSelectedAction: INewsFeedActions["RemoveTopicsSelectedAction"] = async (id) => {
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<TopicRow>>({
      url: `/api/v0.0.3/news-feed/topics/${id}/select`
    });
    return payload;
  };

  public UpdateDigestSettingsAction: INewsFeedActions["UpdateDigestSettingsAction"] = async (data) => {
    await this.api.post<AltiusRes<DigestSettingsForm>>({
      url: "/api/v0.0.3/user/settings",
      data: { type: "digestSettings", key: "digestSettings", data }
    });
    this.dispatch({ type: UserAction.SetDigestSettings, payload: data });
  };

  public UpdateNewsServiceAction: INewsFeedActions["UpdateNewsServiceAction"] = async () => {
    await this.api.post<AltiusRes<ArticlesPageResponse>>({
      url: "/api/v0.0.3/news-feed/user-settings"
    });
  };

  public GetDailyBriefAction: INewsFeedActions["GetDailyBriefAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<dailyBrief>>({
      url: "/api/v0.0.3/news-feed/daily-brief"
    });
    return payload;
  };

  public GetPastDigestsAction: INewsFeedActions["GetPastDigestsAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<DigestRow[]>>({
      url: "/api/v0.0.3/news-feed/digests"
    });
    return payload;
  };
}
